import React, { useState } from 'react';
import { useStore } from '../../../store/useStore';
import { Video, UploadCloud, Folder, Filter, MoreVertical, Plus, Tag, PlayCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { UploadModal } from '../../ui/UploadModal';

export const GaleriaVideos: React.FC = () => {
  const { mediaLibrary, setMediaLibrary } = useStore();
  const [activeFolder, setActiveFolder] = useState('Todos');
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const folders = ['Todos', 'Ambiente', 'Equipe', 'Processos/Produção', 'Depoimentos (UGC)'];

  // Mock data to show the UI/UX
  const mockVideos = [
    { id: '1', title: 'Fachada da Loja', folder: 'Ambiente', duration: '0:15', tags: ['Boa Iluminação', 'Formato Vertical', 'Estável'], thumb: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=400&q=80' },
    { id: '2', title: 'Produção do Bolo', folder: 'Processos/Produção', duration: '0:45', tags: ['Ação', 'Close-up'], thumb: 'https://images.unsplash.com/photo-1556910103-1c02745a872e?auto=format&fit=crop&w=400&q=80' },
    { id: '3', title: 'Cliente Elogiando', folder: 'Depoimentos (UGC)', duration: '1:12', tags: ['Emocional', 'Formato Vertical', 'Alta Conversão'], thumb: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=400&q=80' },
  ];

  const allVideos = mediaLibrary.bRolls?.length > 0 ? mediaLibrary.bRolls : mockVideos;
  const filteredVideos = activeFolder === 'Todos' ? allVideos : allVideos.filter((v: any) => v.folder === activeFolder);

  const handleFileUpload = (files: File[], title: string) => {
    if (files.length > 0) {
      const newVideos = files.map((file, idx) => ({
        id: Math.random().toString(36).substr(2, 9),
        title: files.length > 1 ? `${title} ${idx + 1}` : title,
        folder: activeFolder === 'Todos' ? 'Ambiente' : activeFolder,
        duration: '0:00', // Mock
        tags: ['Upload Recente'],
        thumb: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=400&q=80' // generic video thumb mock
      }));
      
      setMediaLibrary({
        bRolls: [...allVideos, ...newVideos]
      });
      toast.success(`${newVideos.length} vídeo(s) enviado(s) com sucesso!`);
    }
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <UploadModal 
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload de Vídeos (B-Rolls)"
        accept="video/*"
        multiple
        onUpload={handleFileUpload}
      />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Galeria de Vídeos</h3>
          <p className="text-sm text-gray-500 mt-1">B-Rolls, bastidores e UGC. A matéria-prima para Reels e Anúncios.</p>
        </div>
        <button onClick={() => setIsUploadOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
          <UploadCloud size={16} />
          Upload em Lote
        </button>
      </div>

      <div className="flex gap-6 h-[500px]">
        {/* Sidebar Folders */}
        <div className="w-56 shrink-0 flex flex-col gap-1 border-r border-gray-100 pr-6">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-3">Pastas Dinâmicas</h4>
          {folders.map(folder => (
            <button
              key={folder}
              onClick={() => setActiveFolder(folder)}
              className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${activeFolder === folder ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <div className="flex items-center gap-2">
                <Folder size={16} className={activeFolder === folder ? 'text-indigo-500' : 'text-gray-400'} />
                {folder}
              </div>
            </button>
          ))}
          
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 mt-2 transition-colors border border-dashed border-gray-300">
            <Plus size={16} />
            Nova Pasta
          </button>
        </div>

        {/* Video Grid */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {/* Upload Area */}
          <div onClick={() => fileInputRef.current?.click()} className="mb-6 border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center bg-gray-50/50 hover:bg-gray-50 transition-colors cursor-pointer group">
             <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
               <UploadCloud size={24} className="text-indigo-500" />
             </div>
             <p className="text-sm font-medium text-gray-900">Arraste seus vídeos para cá</p>
             <p className="text-xs text-gray-500 mt-1">Suporta MP4, MOV. Múltiplos arquivos permitidos.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredVideos.map((video: any) => (
              <div key={video.id} className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-indigo-300 transition-all shadow-sm hover:shadow-md cursor-pointer relative">
                {/* Thumbnail */}
                <div className="h-40 relative overflow-hidden bg-gray-100">
                   <img src={video.thumb} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                   <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                   <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-1 rounded-md flex items-center gap-1">
                     <PlayCircle size={10} />
                     {video.duration}
                   </div>
                   
                   {/* Info Overlay on Hover */}
                   <div className="absolute inset-0 bg-indigo-900/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-center items-center text-white p-4">
                     <Info size={24} className="mb-2" />
                     <p className="text-xs text-center font-medium">Ver Metadados e Editar</p>
                   </div>
                </div>

                {/* Details */}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h5 className="text-sm font-semibold text-gray-900 line-clamp-1">{video.title}</h5>
                    <button className="text-gray-400 hover:text-gray-600"><MoreVertical size={16} /></button>
                  </div>
                  
                  {/* AI Tags */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {video.tags.map((tag: string) => (
                      <span key={tag} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100">
                        <Tag size={8} />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
